import { Box } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useBlocker, useNavigate, useSearchParams, useOutletContext } from "react-router-dom";
import errorIcon from "../../assets/icons/error-icon.svg";
import BackArrow from "../../assets/icons/left-pointing-arrow-pink.svg";
import scheduleIcon from "../../assets/icons/schedule-icon.svg";
import {
  useCreateCampaign,
  useGetCampaignDetail,
  useSegmentList,
} from "../../services/campaign/index";
import utc from 'dayjs/plugin/utc' // ES 2015
import timezone from 'dayjs/plugin/timezone';
import { useAppMe } from "../../services/okta";
import { useStickyHeader } from "../../shared/hooks/StickyHeader";
import formatValuesCampaign from "../../utils/formatValuesCampaign";
import { BorderSecondary, CircularProgressCustom, PrimaryButton } from "../../utils/muiCustomTheme";
import { CreateCampaignProps } from "../campaigns/campaign-interface";
import DialogComponent from "../createSegment/DialogComponent";
import InsightsWrap from "../createSegment/InsightsWrap";
import SegmentDialog from "../createSegment/SegmentDialog";
import CampaignFormComponent from "./CampaignFormComponent";
import { OutletContext } from "../../App";
import "./CreateCampaigns.scss";
import { useCountryList } from "../../services/okta";
import { Country } from "../../shared/components/dataset-dropdown/DatasetDropdown-interface";
import DatasetDropdownCampaign from "../../shared/components/dataset-dropdown/DatasetDropdownCampaign";
import { useSimulationParams } from "../../shared/hooks/useSimulationParams";
dayjs.extend(utc)
dayjs.extend(timezone)


const CreateCampaign: React.FC<CreateCampaignProps> = () => {
  const [isSegmentDialogOpen, setIsSegmentDialogOpen] = useState(false);
  const { setAlert } = useOutletContext<OutletContext>();

  const navigate = useNavigate();
  const { createLink } = useSimulationParams();
  const { headerRef, sentinelRef, isSticky } = useStickyHeader();
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get("id");

  const [isDiscardByClick, setIsDiscardByClick] = useState(false);

  const { data: dataAppMe, isPending: isPendingAppMe } = useAppMe({});
  const sub = dataAppMe?.data?.user_id;

  const { data: dataCountries, isPending: isPendingCountries } = useCountryList();

  const countryTags = Array.from(new Set(dataCountries?.flatMap((c) => c.tags) || []));
  const adminCountries: Country[] =
    dataCountries
      ?.filter((c: any) => c.admin === 1)
      .map((c: any) => ({
        name: c.name,
        code: c.code ?? c.countryCode ?? "unknown",
        admin: c.admin,
      })) || [];

  const {
    data: dataCampaignDetail,
    isPending: isPendingDataCampaignDetail,
    isFetching: isFetchingDataCampaignDetail,
  } = useGetCampaignDetail({
    query: {
      campaignId: campaignId,
      tags:countryTags, 
      countries: dataCountries?.map((c)=>c.name)
    },
    options: {
      enabled: !!campaignId,
    },
  });

  const parseDataCampaignDetail: any = JSON.parse(dataCampaignDetail?.data || null) || null;
  const {
    campaignName,
    country,
    count = 0,
    engaged = 0,
    reachable = 0,
    uniqueEmails = 0,
    brand,
    folder,
    segments_exc,
    segments_inc,
    build,
  } = parseDataCampaignDetail?.[0] || {};

  const buildParse = JSON.parse(build || null);

  const {
    audienceGroups,
    campaignType,
    endDate,
    quantityValue,
    startDate,
    timeValue,
    deliveryType,
  } = buildParse || {};

  let convertedTimeStart;
  let convertedTimeEnd;
  let convertedStartDate;
  let convertedEndDate;
  let convertedTS;
  let utcTimeStart;
  let utcTimeEnd;

  if (timeValue && startDate){
  utcTimeStart=dayjs.utc(`${timeValue.replace("T","")}`)
  convertedTimeStart=utcTimeStart.tz(dayjs.tz.guess())
  convertedStartDate=convertedTimeStart.format("YYYY-MM-DD")
  convertedTS=convertedTimeStart.format("HH:mm:ss")
  }else {
    convertedStartDate=null
    convertedTS=null
  }

  if(endDate){
    utcTimeEnd=dayjs.utc(`${endDate}${timeValue.split("T")[1]}`)
    convertedTimeEnd=utcTimeEnd.tz(dayjs.tz.guess())
    convertedEndDate=convertedTimeEnd.format("YYYY-MM-DD")
  }else{
    convertedEndDate=null
  }

  const countryCodeFromApi =
    parseDataCampaignDetail?.[0]?.countryCode || parseDataCampaignDetail?.[0]?.code;

  // Find matching admin country, fallback to first admin country
  const defaultCountryCode =
    adminCountries.find((c) => c.code === countryCodeFromApi)?.name ||
    adminCountries[0]?.name || "";

  const tempVal = formatValuesCampaign({
    user: sub,
    campaignName: campaignName,
    campaignType,
    quantityValue,
    country: campaignId ? country : defaultCountryCode,
    deliveryType,
    brand: brand || "Dettol",
    folder: folder || "Dettol",
    afterSpecificNumberOfOccurrences:  quantityValue>0?true:false,
    include: segments_inc?segments_inc.map((i:any)=>i.segmentId) : [],
    exclude: segments_exc?segments_exc.map((i:any)=>i.segmentId) : [],
    type: campaignId ? "edit" : "create",
    audienceGroups: audienceGroups
      ? audienceGroups
      : [
        {
          makeControl: false,
          destinations: [],
          audienceGroup: "Group 1",
          splitPercentage: "100",
        },
      ],
    timeValue: convertedTS,
    startDate:convertedStartDate,
    endDate:convertedEndDate,
    campaignId: campaignId ? campaignId : undefined,
  });

  const methods = useForm<any>({
    defaultValues: tempVal,
    values: tempVal,
  });

  const {
    control,
    formState: { isDirty },
    handleSubmit,
    getValues,
  } = methods;

  const blocker = useBlocker(isDiscardByClick ? false : isDirty);

  const [showUnsavedWorkDialog, setShowUnsavedWorkDialog] = useState<any>({
    show: false,
    type: "discard",
  });

  const selectedCountry = getValues("country");

  const { data: dataSegment, isPending: isPendingDataSegment } = useSegmentList({
    query: { countryName: selectedCountry, tags: countryTags },
    options: {
      enabled: !isPendingCountries && !!selectedCountry && !!countryTags[0], // only fetch when country is available
    },
  });

  const closeUnsavedWorkDialog = () => {
    setShowUnsavedWorkDialog({
      type: "discard",
      show: false,
    });
  };

  const navigateOnCampaigns = () => {
    navigate(createLink("/campaigns"));
  };

  const handleDiscard = () => {
    if (isDirty) {
      setIsDiscardByClick(true);
      setShowUnsavedWorkDialog({
        type: "discard",
        show: true,
      });
    } else {
      navigateOnCampaigns();
    }
  };

  const handleDiscardClick = () => {
    if (showUnsavedWorkDialog.type === "navigate") {
      blocker.proceed?.();
    }
    if (showUnsavedWorkDialog.type === "discard") {
      navigateOnCampaigns(); 
    }
    setShowUnsavedWorkDialog({ show: false, type: "discard" });
  };
  const [selectedTag, setSelectedTag] = useState<string>("");

  // useEffect(() => {
  //   if (buildParse?.tag) {
  //     setSelectedTag(buildParse.tag);
  //   }
  // }, [buildParse]);

  useEffect(() => {
    if (!parseDataCampaignDetail) return;

    const campaignData = parseDataCampaignDetail[0];
    const buildParse = campaignData?.build ? JSON.parse(campaignData.build) : {};

    // To get tag 1st check buildParse, then top-level campaign object
    const initialTag = buildParse?.tag || campaignData?.tag || "";

    setSelectedTag(initialTag);
  }, [parseDataCampaignDetail]);

  const { mutate: createCampaign, isPending: isPendingCreateCampaign } = useCreateCampaign({
    options: {
      onSuccess: (res: any) => {
        const isSuccess = res?.data?.Status === "Success";

        if (isSuccess) {
          navigate(createLink("/campaigns"));
        } else {
          if (res?.data?.Text === "Campaign name already exists") {
            // Set the form error
            methods.setError("campaignName", {
              type: "manual",
              message: "Campaign name already exists",
            });
          } else {
            setAlert({
              alertType: "warning",
              alertMsg: res?.message || "Warning",
            });
          }
        }
      },
      onError: (err: any) => {
        setIsDiscardByClick(false);
        console.error("@err", err);
      },
    },
  });

  const handleSubmitPrimary = () => {
    if (showUnsavedWorkDialog.type === "navigate") {
      blocker.reset?.();
    }
    setShowUnsavedWorkDialog({ show: false, type: "" });
    setIsDiscardByClick(false);
  };

  const openSegmentDialog = () => {
    const totalSplit = getValues("audienceGroups").reduce((accumulator: any, split: any) => {
      return accumulator + Number(split.splitPercentage);
    }, 0);

    if (totalSplit === 100) {
      setIsSegmentDialogOpen(true);
    } else {
      setShowUnsavedWorkDialog({
        type: "total-split-under-100",
        show: true,
      });
    }
  };

  const closeSegmentDialog = () => {
    setIsSegmentDialogOpen(false);
  };

  // block navigation for un-saved work
  useEffect(() => {
    if (blocker.state === "blocked" && isDirty) {
      setShowUnsavedWorkDialog({
        show: true,
        type: "navigate", // distinguish from discard button
      });
    }
  }, [blocker.state, isDirty]);

  const parseSelectedPath = (path: string) => {
    const [country, brand, folder] = (path || "").split("/");
    return { country, brand, folder };
  };

  const onSubmit = (data: any) => {
    setIsDiscardByClick(true);
    const {
      endDate,
      startDate,
      timeValue,
      include,
      exclude,
      audienceGroups,
      campaignName,
      quantityValue,
      afterSpecificNumberOfOccurrences,
      path,
      user,
      type,
      campaignType,
      campaignId,
      deliveryType,
    } = data;

    const parsed = parseSelectedPath(path);

    const tempInclude = dataSegment?.data.filter((item: any) => include.includes(item?.segmentId));
    const tempExclude = dataSegment?.data.filter((item: any) => exclude.includes(item?.segmentId));

    const tempAudienceGroups = audienceGroups.map((item: any) => ({
      ...item,
      splitPercentage: Number(item.splitPercentage),
    }));

    let startTimeVal;
    let endTimeVal;
    let timeVal;
    let cStartDate;
    let cEndDate;

    if (startDate && timeValue) {
      startTimeVal = dayjs.tz(`${startDate} ${timeValue}`, dayjs.tz.guess()).utc()
      timeVal=startTimeVal.format("YYYY-MM-DDTHH:mm:ss");
      cStartDate = startTimeVal.format("YYYY-MM-DD")
    }
    else {
      startTimeVal = dayjs().tz(dayjs.tz.guess()).utc()
      timeVal=startTimeVal.format("YYYY-MM-DDTHH:mm:ss");
      cStartDate = startTimeVal.format("YYYY-MM-DD")
    }

    if (endDate && timeValue){
      endTimeVal = dayjs.tz(`${endDate} ${timeValue}`, dayjs.tz.guess()).utc()
      cEndDate = endTimeVal.format("YYYY-MM-DD");
    }
    else {
      cEndDate=null
    }

    const payload = {
      campaignName,
      campaignType,
      include: tempInclude,
      exclude: tempExclude,
      audienceGroups: tempAudienceGroups,
      format: "JSON",
      quantityValue: Number(quantityValue),
      country: parsed?.country,
      folder: parsed?.folder,
      brand: parsed?.brand,
      afterSpecificNumberOfOccurrences,
      timeValue: timeVal,
      endDate:cEndDate,
      startDate: cStartDate,
      user,
      type,
      campaignId,
      deliveryType,
      tag: selectedTag
    };

    createCampaign(payload);
  };

  if (
    isPendingDataSegment ||
    isPendingAppMe ||
    (isPendingDataCampaignDetail && isFetchingDataCampaignDetail)
  ) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}
      >
        <CircularProgressCustom />
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
      <form>
        <div className="create-campaign">
          <div ref={sentinelRef}></div>

          <div ref={headerRef} className={`create-campaign__wrap ${isSticky ? "is-sticky" : ""}`}>
            <div className="create-campaign__wrap__heading-wrap" onClick={handleDiscard}>
              <div className="create-campaign__wrap__heading-wrap__icon">
                <img src={BackArrow} alt="Back" />
              </div>
              <div className="create-campaign__wrap__heading-wrap__heading">Back to Campaign</div>
            </div>

            <div className="create-campaign__wrap__btn-wrap">
              <div className="create-campaign__wrap__btn-wrap__label">
                {campaignId ? "Edit" : "Create new"} campaign
              </div>
              <div className="create-campaign__wrap__btn">
                <BorderSecondary type="button" onClick={handleDiscard}>
                  Discard
                </BorderSecondary>

                <PrimaryButton
                  variant="contained"
                  onClick={() => !isPendingCreateCampaign && openSegmentDialog()}
                  endIcon={
                    <img
                      className="btn-primary__icon"
                      src={scheduleIcon}
                      alt="Create new campaign"
                    />
                  }
                >
                  {isPendingCreateCampaign ? "Loading..." : "Save and Activate"}
                </PrimaryButton>
              </div>
            </div>

            <div className="create-campaign__dataset-wrapper">
              <div className="home__dataset-sec">
                <Controller
                  control={control}
                  name="country"
                  rules={{ required: "Please provide dataset" }}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <>
                        <div
                          ref={field.ref}
                          className="view-all-attributes__dataset-wrapper__label"
                        >
                          Market:
                        </div>
                        <DatasetDropdownCampaign
                          value={field.value || defaultCountryCode}
                          onChange={field.onChange}
                          countries={adminCountries}
                        />
                        {error?.message && (
                          <div className="create-campaign__input-wrap__segment-err">
                            <img src={errorIcon} alt="Error Icon" />
                            {error.message}
                          </div>
                        )}
                      </>
                    );
                  }}
                />
              </div>
            </div>
            <div className="create-campaign__header-divider"></div>
          </div>

          <InsightsWrap
            dataSegment={dataSegment}
            insights={
              campaignId
                ? [
                  {
                    key: "uniqueConsumers",
                    label: "Unique Consumers",
                    value: count,
                    requiresRefresh: false,
                  },
                  {
                    key: "engagedConsumers",
                    label: "Engaged Consumers",
                    value: engaged,
                    requiresRefresh: false,
                  },
                  {
                    key: "reachableConsumers",
                    label: "Reachable Consumers",
                    value: reachable,
                    requiresRefresh: false,
                  },
                  {
                    key: "uniqueEmails",
                    label: "Unique Emails",
                    value: uniqueEmails,
                    requiresRefresh: false,
                  },
                ]
                : []
            }
            title="Campaign insights:"
          />

          <div className="create-campaign__divider"></div>

          <CampaignFormComponent dataSegment={dataSegment} countryTags={countryTags} />        </div>

        <DialogComponent
          open={showUnsavedWorkDialog.show}
          onClose={closeUnsavedWorkDialog}
          handleSubmitSecondary={handleDiscardClick}
          loading={isPendingCreateCampaign}
          type={showUnsavedWorkDialog.type}
          handleSubmitPrimary={handleSubmitPrimary}
        />

        <SegmentDialog
          open={isSegmentDialogOpen}
          onClose={closeSegmentDialog}
          onSegmentSelect={handleSubmit(onSubmit)}
          actionType="saveCampaign"
          onTagSelect={(tag) => setSelectedTag(tag)}
        />
      </form>
      {(isPendingCreateCampaign) && (
        <div className="freeze-overlay">
          <div className="freeze-overlay__spinner">
            <CircularProgressCustom />
            <div style={{ color: "#333", fontWeight: 500, marginTop: "8px" }}>
              Please wait...
            </div>
          </div>
        </div>
      )}
    </FormProvider>
  );
};

export default CreateCampaign;
